import {useEffect} from "react";
import {RotateCcw} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {notifyError} from "@/api/ErrorNotification.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {useEncounterLights} from "@/encounter/lights/EncounterLightHooks.ts";
import {effectiveLevelLighting} from "@/encounter/lights/EncounterLights.ts";
import DirectionalLightInspector from "@/dungeons/lighting/DirectionalLightInspector.tsx";
import type {DirectionalLight} from "@/dungeons/lighting/DirectionalLight.ts";

export default function EncounterLevelLightingControls() {
    const {dungeon, dungeonSettings} = useDungeon();
    const {state, persistedState, saveLevelLighting, resetLevelLighting,
        previewLevelLighting, clearLevelLightingPreview} = useEncounterLights();
    const level = dungeon.levels.find(item => item.id === dungeonSettings.levelId)!;
    useEffect(() => clearLevelLightingPreview, [level.id, clearLevelLightingPreview]);
    const overridden = persistedState.levelLightingOverrides?.some(item => item.levelId === level.id);
    const persist = async (light: DirectionalLight) => {
        try {await saveLevelLighting(level.id, light);}
        catch (error) {notifyError(error, "Die Grundbeleuchtung konnte nicht gespeichert werden.");}
        finally {clearLevelLightingPreview();}
    };
    const reset = async () => {
        try {await resetLevelLighting(level.id);}
        catch (error) {notifyError(error, "Die Grundbeleuchtung konnte nicht zurückgesetzt werden.");}
        finally {clearLevelLightingPreview();}
    };
    return <section className="space-y-2 border-t pt-3">
        <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{overridden ? "Encounter-Override" : "Dungeon-Standard"}</span>
            <Button size="icon" variant="ghost" disabled={!overridden} title="Dungeon-Standard wiederherstellen"
                aria-label="Grundbeleuchtung zurücksetzen" onClick={() => void reset()}><RotateCcw className="size-4"/></Button>
        </div>
        <DirectionalLightInspector light={effectiveLevelLighting(level.geometry.directionalLight, level.id, state)}
            onChange={light => void persist(light)} onCommit={light => void persist(light)}
            onPreview={light => previewLevelLighting(level.id, light)}/>
    </section>;
}
